import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BUTTON_ICON_OPTIONS } from './buttonBuilderUtils';
import ButtonPresetsBar from './ButtonPresetsBar';
import { flatToNested, nestedToFlat } from './buttonPresets';

// Reusable "extra" button controls (Corners / Hover / Icon + position) that read
// and write FLAT fields on the given object: buttonRadius, buttonHover,
// buttonIcon, buttonIconPosition. `onPatch(patch)` merges the patch back.
// Set `showVariant` to also render the Style variant + gradient-colour picker
// (buttonStyle / buttonGradientColor) for surfaces that carry their own colours.
// Set `showPresets` (default true) to render the shared saved-styles bar.
const ButtonEnhancements = ({ fields = {}, onPatch, showVariant = false, showPresets = true, testPrefix = 'btn' }) => {
  return (
    <div className="space-y-2">
      {showPresets && (
        <ButtonPresetsBar
          currentStyle={flatToNested(fields)}
          onApply={(nested) => onPatch(nestedToFlat(nested))}
        />
      )}
      <div className="flex flex-wrap items-center gap-2">
      {showVariant && (
        <>
          <div className="flex items-center gap-1">
            <label className="text-xs text-slate-500">Style:</label>
            <Select
              value={fields.buttonStyle || 'solid'}
              onValueChange={(v) => onPatch({ buttonStyle: v })}
            >
              <SelectTrigger className="w-24 h-7 text-xs" data-testid={`${testPrefix}-style`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="outline">Outline</SelectItem>
                <SelectItem value="ghost">Ghost</SelectItem>
                <SelectItem value="gradient">Gradient</SelectItem>
                <SelectItem value="soft">Soft</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {fields.buttonStyle === 'gradient' && (
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Gradient:</label>
              <input
                type="color"
                data-testid={`${testPrefix}-gradient-color`}
                value={fields.buttonGradientColor || '#8b5cf6'}
                onChange={(e) => onPatch({ buttonGradientColor: e.target.value })}
                className="w-6 h-6 rounded border cursor-pointer"
              />
            </div>
          )}
        </>
      )}

      <div className="flex items-center gap-1">
        <label className="text-xs text-slate-500">Corners:</label>
        <Select value={fields.buttonRadius || 'rounded'} onValueChange={(v) => onPatch({ buttonRadius: v })}>
          <SelectTrigger className="w-24 h-7 text-xs" data-testid={`${testPrefix}-radius`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="square">Square</SelectItem>
            <SelectItem value="rounded">Rounded</SelectItem>
            <SelectItem value="pill">Pill</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1">
        <label className="text-xs text-slate-500">Hover:</label>
        <Select value={fields.buttonHover || 'fade'} onValueChange={(v) => onPatch({ buttonHover: v })}>
          <SelectTrigger className="w-24 h-7 text-xs" data-testid={`${testPrefix}-hover`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fade">Fade</SelectItem>
            <SelectItem value="darken">Darken</SelectItem>
            <SelectItem value="lift">Lift</SelectItem>
            <SelectItem value="none">None</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1">
        <label className="text-xs text-slate-500">Icon:</label>
        <Select value={fields.buttonIcon || 'none'} onValueChange={(v) => onPatch({ buttonIcon: v })}>
          <SelectTrigger className="w-28 h-7 text-xs" data-testid={`${testPrefix}-icon`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BUTTON_ICON_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {fields.buttonIcon && fields.buttonIcon !== 'none' && (
        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">Pos:</label>
          <Select value={fields.buttonIconPosition || 'right'} onValueChange={(v) => onPatch({ buttonIconPosition: v })}>
            <SelectTrigger className="w-20 h-7 text-xs" data-testid={`${testPrefix}-icon-position`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {fields.buttonIcon && fields.buttonIcon !== 'none' && (
        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">Icon color:</label>
          <input
            type="color"
            data-testid={`${testPrefix}-icon-color`}
            value={fields.buttonIconColor || '#ffffff'}
            onChange={(e) => onPatch({ buttonIconColor: e.target.value })}
            className="w-6 h-6 rounded border cursor-pointer"
          />
          {fields.buttonIconColor && (
            <button
              type="button"
              onClick={() => onPatch({ buttonIconColor: undefined })}
              className="text-[10px] text-slate-400 hover:text-slate-600"
              title="Reset to match text"
            >
              reset
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default ButtonEnhancements;
